import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { CONFIGURATION_AREA_LABELS, USAGE_PLACE_LABELS, USAGE_RHYTHM_LABELS } from '@/lib/company-qualification';
import {
  type AppProposal,
  type ConfigurationArea,
  type ConfigurationProposal,
  type UsagePlace,
  type UsageProposal,
  type UsageRhythm,
  CONFIGURATION_AREAS,
  USAGE_PLACES,
  USAGE_RHYTHMS,
} from '@shared/types/company-qualification';

const SELECT = 'h-9 rounded-lg border border-domino-cardBorder bg-domino-warm px-2 text-sm text-domino-ink';

interface FormProps<T> { onAdd: (entry: T) => void; onCancel: () => void }

const Actions = ({ onCancel, disabled }: { onCancel: () => void; disabled: boolean }) => (
  <div className="flex justify-end gap-2">
    <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Annuler</Button>
    <Button type="submit" size="sm" disabled={disabled}>Ajouter</Button>
  </div>
);

export const ConfigurationForm = ({ onAdd, onCancel }: FormProps<Omit<ConfigurationProposal, 'id' | 'decision'>>) => {
  const [area, setArea] = useState<ConfigurationArea>('profile');
  const [text, setText] = useState('');
  const [why, setWhy] = useState('');
  return (
    <form className="mt-2 space-y-2" onSubmit={(event) => { event.preventDefault(); onAdd({ area, text: text.trim(), why: why.trim() || undefined }); }}>
      <div className="flex gap-2">
        <select aria-label="Domaine" value={area} onChange={(event) => setArea(event.target.value as ConfigurationArea)} className={SELECT}>
          {CONFIGURATION_AREAS.map((key) => <option key={key} value={key}>{CONFIGURATION_AREA_LABELS[key]}</option>)}
        </select>
        <Input value={text} onChange={(event) => setText(event.target.value)} placeholder="Ce qu’il faut configurer" autoFocus />
      </div>
      <Input value={why} onChange={(event) => setWhy(event.target.value)} placeholder="Pourquoi (facultatif)" />
      <Actions onCancel={onCancel} disabled={!text.trim()} />
    </form>
  );
};

export const AppForm = ({ onAdd, onCancel }: FormProps<Omit<AppProposal, 'id' | 'decision'>>) => {
  const [fields, setFields] = useState({ title: '', bottleneck: '', cost: '', measure: '', truthSet: '', expected: '' });
  const set = (key: keyof typeof fields) => (event: { target: { value: string } }) => setFields({ ...fields, [key]: event.target.value });
  const optional = (value: string) => value.trim() || undefined;
  return (
    <form
      className="mt-2 space-y-2"
      onSubmit={(event) => {
        event.preventDefault();
        onAdd({
          title: fields.title.trim(), bottleneck: fields.bottleneck.trim(), measure: fields.measure.trim(),
          cost: optional(fields.cost), truthSet: optional(fields.truthSet), expected: optional(fields.expected),
        });
      }}
    >
      <Input value={fields.title} onChange={set('title')} placeholder="Nom de l’application" autoFocus />
      <Input value={fields.bottleneck} onChange={set('bottleneck')} placeholder="Goulot servi" />
      <Input value={fields.cost} onChange={set('cost')} placeholder="Ce que coûte ce goulot (facultatif)" />
      <Input value={fields.measure} onChange={set('measure')} placeholder="Mesure de réussite" />
      <Input value={fields.truthSet} onChange={set('truthSet')} placeholder="Jeu de vérité : exemples jugés par le référent (facultatif)" />
      <Input value={fields.expected} onChange={set('expected')} placeholder="Résultat attendu (facultatif)" />
      <Actions onCancel={onCancel} disabled={!fields.title.trim() || !fields.bottleneck.trim() || !fields.measure.trim()} />
    </form>
  );
};

export const UsageForm = ({ onAdd, onCancel }: FormProps<Omit<UsageProposal, 'id' | 'decision'>>) => {
  const [rhythm, setRhythm] = useState<UsageRhythm>('daily');
  const [place, setPlace] = useState<UsagePlace>('today');
  const [who, setWho] = useState('');
  const [what, setWhat] = useState('');
  return (
    <form className="mt-2 space-y-2" onSubmit={(event) => { event.preventDefault(); onAdd({ kind: 'ritual', rhythm, place, who: who.trim(), what: what.trim() }); }}>
      <div className="flex flex-wrap gap-2">
        <select aria-label="Rythme" value={rhythm} onChange={(event) => setRhythm(event.target.value as UsageRhythm)} className={SELECT}>
          {USAGE_RHYTHMS.map((key) => <option key={key} value={key}>{USAGE_RHYTHM_LABELS[key]}</option>)}
        </select>
        <select aria-label="Où" value={place} onChange={(event) => setPlace(event.target.value as UsagePlace)} className={SELECT}>
          {USAGE_PLACES.map((key) => <option key={key} value={key}>{USAGE_PLACE_LABELS[key]}</option>)}
        </select>
        <Input value={who} onChange={(event) => setWho(event.target.value)} placeholder="Qui" className="min-w-[8rem] flex-1" autoFocus />
      </div>
      <Input value={what} onChange={(event) => setWhat(event.target.value)} placeholder="Ce qu’il fait dans Domino" />
      <Actions onCancel={onCancel} disabled={!who.trim() || !what.trim()} />
    </form>
  );
};
